import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, of, timeout } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface TenantResolutionResponse {
  tenantId?: string;
  idpHint?: string;
}

const RESOLUTION_TIMEOUT_MS = 2000;

/**
 * Resolves the current browser host to a Keycloak `kc_idp_hint` so the login
 * flow can skip the realm-level identity-provider chooser when the tenant
 * has a configured broker (e.g. Entra). Powered by the unauthenticated
 * `GET /bot/api/v1/public/tenant-resolution` endpoint, which reads from the
 * Helm-rendered tenant routing ConfigMap mounted into mgmt-api.
 *
 * Failure modes are deliberately silent — if the lookup errors, times out,
 * or returns no hint, login falls back to the standard chooser flow.
 */
@Injectable({ providedIn: 'root' })
export class TenantResolutionService {
  private readonly http = inject(HttpClient);

  private _idpHint: string | null = null;
  private _expectedTenantId: string | null = null;
  private _resolved = false;

  /** Keycloak broker alias for this tenant URL, or null if the host isn't configured. */
  get idpHint(): string | null { return this._idpHint; }
  /** Tenant the *current URL* is branded for; null if the host has no entry. */
  get expectedTenantId(): string | null { return this._expectedTenantId; }
  get hasIdpHint(): boolean { return this._idpHint !== null; }

  /**
   * Look up the current host once at app boot. Network errors and timeouts
   * leave the hint unset and let the standard login flow run unchanged.
   * Idempotent — subsequent calls are a no-op.
   */
  async resolve(): Promise<void> {
    if (this._resolved) return;
    this._resolved = true;

    const host = window.location.host;
    if (!host) return;

    const result = await firstValueFrom(
      this.http
        .get<TenantResolutionResponse>('/bot/api/v1/public/tenant-resolution', { params: { host } })
        .pipe(
          timeout(RESOLUTION_TIMEOUT_MS),
          catchError(() => of<TenantResolutionResponse>({})),
        ),
    );

    if (result?.idpHint) {
      this._idpHint = result.idpHint;
    }
    if (result?.tenantId) {
      this._expectedTenantId = result.tenantId;
    }
  }
}
